import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useOutlet, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { PublicPortalAppearanceProvider, usePublicPortalAppearance } from '../../context/PublicPortalAppearanceContext';
import { resolveBrandingLogoImageUrl } from '../../utils/publicAssetUrl';
import { getPublicApiBase } from '../../utils/publicApi';
import { menuOutlinedBoxButtonSx } from '../../utils/landingThemeHelpers';
import { PublicBrandingIdentity } from './PublicBrandingIdentity';
import PublicSiteAccessButtons from './PublicSiteAccessButtons';
import PortalTemplateSelector from './PortalTemplateSelector';
import AuthorizedAccessDisclaimer from './AuthorizedAccessDisclaimer';

function sectionMenuHelp(companyName) {
  const company = (companyName && String(companyName).trim()) || 'our company';
  return {
    jobs: `Jobs — Query all the jobs of ${company} to apply for the company or their clients. This includes contract jobs, full-time jobs, contract-to-hire, part-time, and more.`,
    trainings: `Trainings — Explore learning programs and courses from ${company}. Build skills with structured training paths, materials, and instructor-led content.`,
    exams: `Exams — Take assessments and certification exams published by ${company}. Measure readiness for roles, courses, and skill checkpoints.`,
    benches: `Benches — View available bench talent and consultants listed by ${company}. Review profiles for staffing, placement, and client project needs.`
  };
}

/** Map URL hash → browsing section menu key (for sparkle). */
function browsingSectionFromHash(hash) {
  const h = String(hash || '').replace(/^#/, '');
  if (!h || h === 'portal-jobs') return 'jobs';
  if (h === 'portal-training' || h.startsWith('portal-training-cat-')) return 'trainings';
  if (h === 'portal-exams') return 'exams';
  if (h === 'portal-benches') return 'benches';
  return null;
}

function PublicLayoutChrome({ portalBasePath = '/query', children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const embedMode = searchParams.get('embed') === '1';
  const outlet = useOutlet();
  const portalHome = portalBasePath || '/query';
  const marketingHome = '/';
  const section = (id) => `${portalHome}#${id}`;
  const activeBrowsingSection = browsingSectionFromHash(location.hash);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const { templateId, pageBackgroundCss, publicBranding } = usePublicPortalAppearance();

  const logoSrc = publicBranding.portalLogoPath
    ? resolveBrandingLogoImageUrl(publicBranding.portalLogoPath)
    : null;

  const [trainMenuAnchor, setTrainMenuAnchor] = useState(null);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [benchesPublic, setBenchesPublic] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/training/catalog`)
      .then((r) => {
        const d = r.data?.data;
        if (cancelled || !d?.enabled || !Array.isArray(d.categories)) return;
        setTrainingCategories(d.categories);
      })
      .catch(() => {
        if (!cancelled) setTrainingCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/visibility`)
      .then((r) => {
        const v = r.data?.data || {};
        if (!cancelled) setBenchesPublic(v.benches === 'public');
      })
      .catch(() => {
        if (!cancelled) setBenchesPublic(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/signup-config`)
      .then((r) => {
        if (!cancelled) setSignupEnabled(r.data?.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setSignupEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const closeTrainMenu = () => setTrainMenuAnchor(null);

  const barIsLight = ['light', 'aqua', 'orchid'].includes(templateId);
  const menuHelp = sectionMenuHelp(publicBranding.companyName);
  const tooltipProps = {
    enterDelay: 350,
    leaveDelay: 80,
    arrow: true,
    placement: 'bottom',
    slotProps: {
      tooltip: {
        sx: {
          maxWidth: 360,
          fontSize: '0.8rem',
          lineHeight: 1.45,
          fontWeight: 500,
          px: 1.25,
          py: 1
        }
      }
    }
  };

  if (embedMode) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: pageBackgroundCss,
          backgroundAttachment: 'fixed'
        }}
      >
        {children ?? outlet}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PortalTemplateSelector />

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: barIsLight ? alpha('#ffffff', 0.9) : alpha('#0f172a', 0.88),
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: barIsLight ? 'text.primary' : 'grey.100',
          borderBottom: '1px solid',
          borderColor: barIsLight ? alpha('#0f172a', 0.08) : alpha('#fff', 0.1),
          boxShadow: barIsLight ? '0 8px 32px rgba(15,23,42,0.08)' : '0 8px 32px rgba(0,0,0,0.35)'
        }}
      >
        <Toolbar
          sx={{
            gap: { xs: 1, sm: 1.5 },
            flexWrap: 'wrap',
            py: 1.25,
            alignItems: 'center'
          }}
        >
          {/* Branding — left */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexShrink: 0,
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 280, md: 320 },
              cursor: 'pointer'
            }}
            onClick={() => navigate(marketingHome)}
          >
            <PublicBrandingIdentity
              variant="toolbar"
              toolbarColorScheme={barIsLight ? 'light' : 'dark'}
              logoSrc={logoSrc}
              companyName={publicBranding.companyName}
              companyAddress={publicBranding.companyAddress}
              companyCity={publicBranding.companyCity}
              companyZip={publicBranding.companyZip}
              companyWebsite={publicBranding.companyWebsite}
              companyContactEmail={publicBranding.companyContactEmail}
              companyTagline={publicBranding.companyTagline}
              companyNameStyle={publicBranding.companyNameStyle}
              companyTaglineStyle={publicBranding.companyTaglineStyle}
            />
          </Box>

          {/* Section menu — after logo, center-justified, equal-size boxes */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 1.25,
              flex: '1 1 auto',
              minWidth: 0,
              px: { xs: 0, sm: 1 }
            }}
          >
            <Tooltip title={menuHelp.jobs} {...tooltipProps}>
              <Button
                component={RouterLink}
                to={section('portal-jobs')}
                variant="outlined"
                size="small"
                sx={menuOutlinedBoxButtonSx({
                  tone: 'jobs',
                  uniform: true,
                  active: activeBrowsingSection === 'jobs'
                })}
              >
                Jobs
              </Button>
            </Tooltip>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Tooltip title={menuHelp.trainings} {...tooltipProps}>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowDropDownIcon sx={{ fontSize: 18 }} />}
                  onClick={(e) => {
                    setTrainMenuAnchor(e.currentTarget);
                    navigate(section('portal-training'));
                  }}
                  sx={menuOutlinedBoxButtonSx({
                    tone: 'trainings',
                    uniform: true,
                    active: activeBrowsingSection === 'trainings'
                  })}
                >
                  Trainings
                </Button>
              </Tooltip>
              <Menu
                anchorEl={trainMenuAnchor}
                open={Boolean(trainMenuAnchor)}
                onClose={closeTrainMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { minWidth: 220 } } }}
              >
                <MenuItem
                  component={RouterLink}
                  to={section('portal-training')}
                  onClick={closeTrainMenu}
                >
                  All trainings
                </MenuItem>
                {trainingCategories.map((cat) => (
                  <MenuItem
                    key={cat.id}
                    component={RouterLink}
                    to={`${portalHome}#portal-training-cat-${cat.id}`}
                    onClick={closeTrainMenu}
                  >
                    {cat.name}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            <Tooltip title={menuHelp.exams} {...tooltipProps}>
              <Button
                component={RouterLink}
                to={section('portal-exams')}
                variant="outlined"
                size="small"
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  ...menuOutlinedBoxButtonSx({
                    tone: 'exams',
                    uniform: true,
                    active: activeBrowsingSection === 'exams'
                  })
                }}
              >
                Exams
              </Button>
            </Tooltip>
            {benchesPublic ? (
              <Tooltip title={menuHelp.benches} {...tooltipProps}>
                <Button
                  component={RouterLink}
                  to={section('portal-benches')}
                  variant="outlined"
                  size="small"
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    ...menuOutlinedBoxButtonSx({
                      tone: 'benches',
                      uniform: true,
                      active: activeBrowsingSection === 'benches'
                    })
                  }}
                >
                  Benches
                </Button>
              </Tooltip>
            ) : null}
          </Box>

          {/* Access actions — right: Home · Sign In · Register · Browsing (Browsing sparkles when active) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.25,
              flexShrink: 0,
              ml: { xs: 0, md: 'auto' }
            }}
          >
            <PublicSiteAccessButtons
              active="query"
              size="small"
              signupEnabled={signupEnabled}
            />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          background: pageBackgroundCss,
          backgroundAttachment: 'fixed'
        }}
      >
        {children ?? outlet}
        <Box component="footer" sx={{ flexShrink: 0, py: 2.5, px: 2 }}>
          <AuthorizedAccessDisclaimer companyName={publicBranding.companyName} />
        </Box>
      </Box>
    </Box>
  );
}

const PublicLayout = ({ portalBasePath = '/query', children }) => (
  <PublicPortalAppearanceProvider>
    <PublicLayoutChrome portalBasePath={portalBasePath}>{children}</PublicLayoutChrome>
  </PublicPortalAppearanceProvider>
);

export default PublicLayout;
